import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import {
    DaHelp,
    DaInput,
    DaLabel,
    QuestionBoldIcon,
    InternalGrid,
    Text,
    Message,
    Box,
    Link,
    ButtonGroup,
} from '@paygreen/paygreen-ui';
import {
    FormControlWrapper,
    StatusDisplayTable,
    EditButton,
} from '../../../components';
import { addManuallyInputsPatterns } from '../shared/matchingInputsUtils';
import { statusOptions } from '../shared/matchingInputsUtils';
import { IsMobile } from '../../../utils/tools';

const NameProductForm = ({
    t,
    defaultValues,
    children,
    isOpen,
    setDisplayCategoryForm,
    selectedProduct,
    isCategory,
    ...rest
}) => {
    const { register, errors, setValue, getValues } = useFormContext();

    const handleChange = ({ target: { name, value } }) => {
        setValue(name, value);
    };

    // to switch properly between inputs with or without defaultValues when adding or editing a product
    const switchInputs = (name) => {
        const inputPropsControlled = {
            value: getValues()[name],
        };

        const inputPropsUncontrolled = {
            inputRef: register(),
            defaultValue: getValues()[name] || defaultValues[name],
        };

        return defaultValues[name] && selectedProduct
            ? inputPropsUncontrolled
            : inputPropsControlled;
    };

    const hasMessage = (element) => {
        return (
            element.name === 'productExternalReference' &&
            !defaultValues.productExternalReference
        );
    };

    return (
        <Box {...rest}>
            <InternalGrid
                displayType="grid"
                gridGap="md"
                alignItems="stretch"
                justifyItems="stretch"
                gridTemplateColumns="1fr"
            >
                {!isCategory &&
                    addManuallyInputsPatterns.slice(0, 2).map((element) => (
                        <FormControlWrapper
                            key={element.name}
                            inputName={element.name}
                            isRequired={
                                element.name === 'productName' ||
                                !defaultValues.productExternalReference
                            }
                            errors={errors}
                            i18labelBase="matching.addManuallyForm"
                            hasStaticWidth
                            hasHelpButton={hasMessage(element)}
                            hasMessage={isOpen} // to allow to hide message when we close modal
                        >
                            <DaInput
                                onChange={handleChange}
                                name={element.name}
                                id={
                                    element.name +
                                    defaultValues.productExternalReference
                                }
                                type={element.type}
                                inputRef={register({
                                    required:
                                        element.name !== 'emissionExternalId'
                                            ? t('form.error.required')
                                            : null,
                                    pattern: {
                                        value: element.pattern,
                                        message: t(
                                            `form.error.${element.name}`,
                                        ),
                                    },
                                })}
                                defaultValue={defaultValues[element.name]}
                                blockWidth="lg"
                                disabled={
                                    element.name ===
                                        'productExternalReference' &&
                                    !!defaultValues.productExternalReference
                                }
                            />

                            {hasMessage(element) && (
                                <DaHelp>
                                    <QuestionBoldIcon />
                                </DaHelp>
                            )}

                            {hasMessage(element) && (
                                <Text textSize="xxs" colorWab="grey40">
                                    {t(
                                        'matching.addManuallyForm.productExternalReferenceWarning',
                                    )}
                                </Text>
                            )}
                            <Message
                                arrowBlock="topRight"
                                hasStaticWidth={true}
                            >
                                <Text textSize="xs" align="center">
                                    {t(
                                        'matching.addManuallyForm.productExternalReferenceHelp',
                                    )}
                                </Text>
                            </Message>
                        </FormControlWrapper>
                    ))}

                {isCategory && (
                    <FormControlWrapper
                        key="categoryName"
                        inputName="categoryName"
                        isRequired
                        errors={errors}
                        i18labelBase="matching.addManuallyForm"
                        hasStaticWidth
                    >
                        <DaInput
                            onChange={handleChange}
                            name="categoryName"
                            id="categoryName"
                            type="text"
                            inputRef={register({
                                required: t('form.error.required'),
                            })}
                            defaultValue={defaultValues['categoryName']}
                            blockWidth="lg"
                            disabled
                        />
                    </FormControlWrapper>
                )}
            </InternalGrid>

            {!!setDisplayCategoryForm && (
                <Box marginTop="sm">
                    <DaLabel
                        htmlFor={addManuallyInputsPatterns.emissionFactorName}
                    >
                        {t(
                            'matching.addManuallyForm' +
                                '.' +
                                'emissionFactorName' +
                                'Input',
                        )}
                    </DaLabel>

                    <InternalGrid
                        displayType={selectedProduct ? 'grid' : 'flex'}
                        gridTemplateColumns={
                            IsMobile() && selectedProduct ? '1fr' : '1fr auto'
                        }
                        gridGap="md"
                        justifyItems="stretch"
                        alignItems="center"
                    >
                        {!selectedProduct ? (
                            <StatusDisplayTable
                                statusOptions={statusOptions}
                                status="NO_MATCH"
                                textSize="sm"
                                i18TextBase="matching.table.status"
                                style={{ justifyContent: 'flex-start' }}
                            />
                        ) : (
                            <div>
                                {(getValues()['emissionFactorName'] ||
                                    defaultValues['emissionFactorName']) && (
                                    <DaInput
                                        name={'emissionFactorName'}
                                        id={
                                            'emissionFactorName' +
                                            defaultValues.productExternalReference
                                        }
                                        type="text"
                                        blockWidth="lg"
                                        disabled={true}
                                        {...switchInputs('emissionFactorName')}
                                    />
                                )}
                                {!getValues()['emissionFactorName'] &&
                                    !defaultValues['emissionFactorName'] &&
                                    (getValues()['monetaryRatioName'] ||
                                        defaultValues['monetaryRatioName']) && (
                                        <DaInput
                                            name={'monetaryRatioName'}
                                            id={
                                                'monetaryRatioName' +
                                                defaultValues.productExternalReference
                                            }
                                            type="text"
                                            blockWidth="lg"
                                            disabled={true}
                                            {...switchInputs(
                                                'monetaryRatioName',
                                            )}
                                        />
                                    )}

                                {getValues().documentation && (
                                    <a
                                        href={getValues().documentation}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Text textSize="xxs">
                                            <Link>
                                                Source {getValues().source}
                                            </Link>
                                        </Text>
                                    </a>
                                )}
                            </div>
                        )}

                        <ButtonGroup
                            marginBottom={
                                getValues().documentation ? 'sm' : 'none'
                            }
                        >
                            <EditButton
                                colorStatus="success"
                                buttonSize="sm"
                                buttonStyle="line"
                                onClick={() => setDisplayCategoryForm(true)}
                            >
                                {selectedProduct
                                    ? t('form.button.edit')
                                    : t(
                                          'matching.addManuallyForm.defineButton',
                                      )}
                            </EditButton>
                        </ButtonGroup>
                    </InternalGrid>
                </Box>
            )}
        </Box>
    );
};

NameProductForm.propTypes = {
    defaultValues: PropTypes.object,
    selectedProduct: PropTypes.string,
    isOpen: PropTypes.bool,
    isCategory: PropTypes.bool,
};

NameProductForm.defaultProps = {
    selectedProduct: '',
    isOpen: true,
    isCategory: false,
};
export default withTranslation('common')(NameProductForm);
